import { useMemo, useState } from 'react'
import { Modal, Button } from './ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder, faHouse } from '@fortawesome/free-solid-svg-icons'
import type { Folder } from '../types'
import { colors, fontSize, radius, spacing } from '../styles/theme'
import { pathDepth } from '../utils/folderTree'

interface MoveToFolderModalProps {
  /** What is being moved, for the dialog copy. */
  itemName: string
  folders: Folder[]
  /** Folder the item currently lives in, so it can be shown as a no-op. */
  currentFolderId: string | null
  /**
   * When moving a folder, its own path — its whole subtree is excluded from the target list, and
   * so is anything that would push a descendant past the depth limit.
   */
  movingFolderPath?: string
  /** Deepest depth in the moved folder's subtree, for the same reason. */
  movingSubtreeMaxDepth?: number
  maxDepth: number
  loading?: boolean
  onMove: (targetFolderId: string | null) => void
  onClose: () => void
}

/**
 * Keyboard- and screen-reader-accessible alternative to dragging, and the practical way to move
 * something into a far-off branch without a long drag.
 */
export default function MoveToFolderModal({
  itemName, folders, currentFolderId, movingFolderPath, movingSubtreeMaxDepth,
  maxDepth, loading = false, onMove, onClose,
}: MoveToFolderModalProps) {
  const [target, setTarget] = useState<string | null>(null)

  const options = useMemo(() => {
    const subtreeHeight = movingFolderPath && movingSubtreeMaxDepth
      ? movingSubtreeMaxDepth - pathDepth(movingFolderPath)
      : 0

    return folders
      .filter(f => {
        if (movingFolderPath && f.path.startsWith(movingFolderPath)) return false  // own subtree
        return pathDepth(f.path) + 1 + subtreeHeight <= maxDepth
      })
      .map(f => ({ folder: f, depth: pathDepth(f.path) }))
      .sort((a, b) => a.folder.path.localeCompare(b.folder.path))
  }, [folders, movingFolderPath, movingSubtreeMaxDepth, maxDepth])

  const rowStyle = (selected: boolean, disabled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: spacing.xs,
    width: '100%',
    padding: `6px ${spacing.sm}`,
    border: 'none',
    borderRadius: radius.sm,
    textAlign: 'left',
    fontSize: fontSize.sm,
    cursor: disabled ? 'default' : 'pointer',
    color: disabled ? colors.textMuted : selected ? colors.text : colors.textSecondary,
    background: selected ? colors.primaryLight : 'transparent',
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Move to folder"
      subtitle={`Choose a new location for "${itemName}".`}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => onMove(target)}
            loading={loading}
            disabled={target === currentFolderId}
          >
            Move
          </Button>
        </>
      }
    >
      <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <button
          onClick={() => setTarget(null)}
          disabled={currentFolderId === null}
          style={rowStyle(target === null, currentFolderId === null)}
        >
          <FontAwesomeIcon icon={faHouse} style={{ fontSize: 11, color: colors.textMuted }} />
          All Templates
          {currentFolderId === null && <span style={{ marginLeft: 'auto', fontSize: fontSize.xs }}>current</span>}
        </button>

        {options.map(({ folder, depth }) => {
          const isCurrent = folder.folderId === currentFolderId
          return (
            <button
              key={folder.folderId}
              onClick={() => setTarget(folder.folderId)}
              disabled={isCurrent}
              // Numeric, not `spacing.sm + ...` — the token is the string '8px', which would
              // concatenate into invalid CSS and drop the indent entirely.
              style={{ ...rowStyle(target === folder.folderId, isCurrent), paddingLeft: 8 + depth * 16 }}
            >
              <FontAwesomeIcon icon={faFolder} style={{ fontSize: 11, color: colors.textMuted }} />
              {folder.name}
              {isCurrent && <span style={{ marginLeft: 'auto', fontSize: fontSize.xs }}>current</span>}
            </button>
          )
        })}
      </div>
    </Modal>
  )
}
